import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Clock, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { paymentService } from '@/lib/services/payment.service';
import { useStore } from '@/contexts/StoreContext';
import { getStoreDomain } from '@/lib/utils/store-domain';
import { useTextosDaLoja } from '@/construtor/textos';
import { Caixa } from '@/construtor/caixa';

export function PaymentPending() {
  const { t } = useTextosDaLoja();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { store } = useStore();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const attemptsRef = useRef(0);

  const paymentId = searchParams.get('payment_id');
  const externalReference = searchParams.get('external_reference');

  const [status, setStatus] = useState<string>('pending');
  const [stopped, setStopped] = useState(false);

  useEffect(() => {
    if (!externalReference || !store || !store.domains || store.domains.length === 0) {
      setStopped(true);
      return;
    }

    const primaryDomain = getStoreDomain(store);

    if (!primaryDomain) {
      setStopped(true);
      return;
    }

    // Cleanup defensivo — evita múltiplos setInterval se o effect re-rodar.
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    attemptsRef.current = 0;

    let cancelled = false;
    let consecutiveErrors = 0;

    const stop = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const checkStatus = async () => {
      if (cancelled) return;
      try {
        const result = await paymentService.getPaymentStatusByOrderId(
          externalReference,
          primaryDomain,
        );
        consecutiveErrors = 0;
        const normalized = result.payment.status?.toUpperCase();

        // Pedido expirado: o pagamento não vale mais, para de esperar.
        if (result.orderStatus === 'EXPIRED') {
          setStatus('EXPIRED');
          stop();
          return;
        }

        setStatus(normalized);

        if (normalized === 'APPROVED') {
          stop();
          setTimeout(() => {
            if (!cancelled) navigate(`/payment/success?payment_id=${result.payment.id}`);
          }, 2000);
          return;
        }

        if (
          normalized === 'DECLINED' ||
          normalized === 'FAILED' ||
          normalized === 'CANCELLED'
        ) {
          stop();
          setTimeout(() => {
            if (!cancelled) navigate(`/payment/failure?payment_id=${result.payment.id}`);
          }, 2000);
          return;
        }
      } catch (err) {
        consecutiveErrors += 1;
        // eslint-disable-next-line no-console
        console.debug('[PaymentPending] erro ao consultar status:', err);
        if (consecutiveErrors >= 12) {
          // ~1 min de falhas consecutivas — para de tentar pra não bater com erro infinito.
          stop();
          setStopped(true);
        }
      }

      attemptsRef.current += 1;
      if (attemptsRef.current >= 120) {
        stop();
        setStopped(true);
      }
    };

    checkStatus();
    intervalRef.current = setInterval(checkStatus, 5000);

    return () => {
      cancelled = true;
      stop();
    };
  }, [externalReference, store]);

  const getStatusIcon = () => {
    const s = status?.toUpperCase();
    if (s === 'APPROVED') return <CheckCircle className="w-12 h-12 text-success" />;
    if (s === 'DECLINED' || s === 'FAILED' || s === 'CANCELLED' || s === 'EXPIRED')
      return <XCircle className="w-12 h-12 text-destructive" />;
    return <Clock className="w-12 h-12 text-warning" />;
  };

  const getStatusText = () => {
    const s = status?.toUpperCase();
    if (s === 'APPROVED') return t('payment.pending.approved_redirect');
    if (s === 'EXPIRED') return t('store.cart.order_expired');
    if (s === 'DECLINED' || s === 'FAILED' || s === 'CANCELLED') return t('payment.pending.declined');
    return t('payment.pending.title');
  };

  const isPending =
    !status ||
    status.toUpperCase() === 'PENDING' ||
    status.toUpperCase() === 'PROCESSING';

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Caixa id="pagamento.pendente.cartao" miolo>
      <Card className="max-w-md w-full">
        <CardContent className="pt-6 text-center space-y-4">
          <Caixa id="pagamento.pendente.icone">
          <div className="flex justify-center">
            <div className="w-20 h-20 rounded-full bg-warning/20 flex items-center justify-center">
              {getStatusIcon()}
            </div>
          </div>
          </Caixa>

          <Caixa id="pagamento.pendente.titulo">
          <h1 className="text-2xl font-bold text-foreground">{getStatusText()}</h1>
          </Caixa>

          {isPending && !stopped && (
            <>
              <Caixa id="pagamento.pendente.mensagem">
              <p className="text-muted-foreground">{t('payment.pending.desc')}</p>
              </Caixa>
              <Caixa id="pagamento.pendente.aviso">
              <div className="bg-warning/10 border border-warning/30 p-4 rounded-lg">
                <div className="flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-warning" />
                  <p className="text-sm text-warning">{t('payment.pending.checking')}</p>
                </div>
              </div>
              </Caixa>
            </>
          )}

          {paymentId && (
            <Caixa id="pagamento.pendente.referencia">
            <div className="bg-muted p-3 rounded-lg text-sm">
              <p className="text-muted-foreground">{t('payment.pending.payment_id')}</p>
              <p className="font-mono font-semibold">{paymentId}</p>
            </div>
            </Caixa>
          )}

          {(stopped || (!isPending && status?.toUpperCase() !== 'APPROVED')) && (
            <Caixa id="pagamento.pendente.botoes">
            <div className="space-y-2 pt-4">
              <Button onClick={() => navigate('/')} className="w-full">
                {t('payment.pending.back_to_store')}
              </Button>
            </div>
            </Caixa>
          )}
        </CardContent>
      </Card>
      </Caixa>
    </div>
  );
}
